/**
 * Generates a unique CSS selector for a given element.
 * Strategy:
 * 1. If element has an id → #myId
 * 2. If element has data-testid → [data-testid="value"]
 * 3. Otherwise build a path from tag.class:nth-child(n)
 * 4. Verify uniqueness via querySelectorAll
 */
export function generateUniqueSelector(element: HTMLElement): string {
  // Strategy 1: id
  if (element.id) {
    const selector = `#${CSS.escape(element.id)}`;
    if (isUnique(selector)) return selector;
  }

  // Strategy 2: data-testid
  const testId = element.getAttribute('data-testid');
  if (testId) {
    const selector = `[data-testid="${CSS.escape(testId)}"]`;
    if (isUnique(selector)) return selector;
  }

  // Strategy 3: build path
  const path: string[] = [];
  let current: HTMLElement | null = element;

  while (current && current !== document.documentElement && current !== document.body) {
    let segment = current.tagName.toLowerCase();

    if (current.id) {
      segment = `#${CSS.escape(current.id)}`;
      path.unshift(segment);
      break;
    }

    // Add classes (up to 3)
    const classes = Array.from(current.classList).slice(0, 3);
    if (classes.length > 0) {
      segment += classes.map(c => `.${CSS.escape(c)}`).join('');
    }

    // Add nth-child if needed for specificity
    const parent = current.parentElement;
    if (parent) {
      const sameTagSiblings = Array.from(parent.children).filter(
        c => c.tagName === current!.tagName
      );
      if (sameTagSiblings.length > 1) {
        const childIndex = Array.from(parent.children).indexOf(current) + 1;
        segment += `:nth-child(${childIndex})`;
      }
    }

    path.unshift(segment);

    // Check if we're unique enough already
    const candidate = path.join(' > ');
    if (isUnique(candidate)) return candidate;

    current = current.parentElement;
  }

  return path.join(' > ');
}

/**
 * Generates an XPath for a given element.
 */
export function generateXPath(element: HTMLElement): string {
  const parts: string[] = [];
  let current: Node | null = element;

  while (current && current.nodeType === Node.ELEMENT_NODE) {
    let index = 0;
    let sibling: Node | null = current.previousSibling;

    while (sibling) {
      if (
        sibling.nodeType === Node.ELEMENT_NODE &&
        (sibling as Element).tagName === (current as Element).tagName
      ) {
        index++;
      }
      sibling = sibling.previousSibling;
    }

    const tag = (current as Element).tagName.toLowerCase();
    const part = index > 0 ? `${tag}[${index + 1}]` : tag;
    parts.unshift(part);

    current = (current as Element).parentNode;
    if (current === document) break;
  }

  return '/' + parts.join('/');
}

function isUnique(selector: string): boolean {
  try {
    return document.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}
